import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import {
  buildActivityRequestParams,
  mapActivityRequestCounters,
  mapActivityRequestListResponse,
  mapActivityRequestTimelineResponse
} from '@/Network/Services/activityRequestsApi.mapper'
import type {
  ApiActivityRequestCounters,
  ApiActivityRequestListResponse,
  ApiActivityRequestTimelineResponse
} from '@/Network/Services/activityRequestsApi.mapper'
import type {
  ModuleViewCounters,
  ModuleViewListResponse
} from '@/components/designSystem/QueueViews/types'
import { ACTIVITY_REQUESTS_API_PATH } from '@/constants/activity-requests.constants'
import type {
  ActivityRequestPayload,
  ActivityRequestQuery,
  ActivityRequestTimelineResponse
} from '@/types/activity-requests.types'
import { ServiceError, getApiErrorStatus, isTimeoutError } from '@/utils/apiError'
import type {
  ActiveModality,
  CreateDSAttendancePolicyPayload,
  DSAttendancePolicyData,
  UpdateDSAttendancePolicyPayload,
  DSAttendanceContainer,
  DSAttendanceContainerFilterParams,
  ListContainersByCompanyParams,
  DSAttendanceHybridDetail
} from '@/types/ds-attendance.types'
import {
  ActivityType,
  CheckMethod,
  CoveredLegs,
  DSAttendanceMonitoringType,
  DSAttendanceDeadlineCalculationType,
  DSAttendanceRuleRecurrence,
  DSAttendanceStatus,
  EffectiveRegistrationStatus,
  EffectiveSourceKind
} from '@/constants/ds-attendance.constants'
import type { AttachmentExportFormat, ExportNamePart } from '@/constants/ds-attendance.constants'
import {
  DEFAULT_AUTO_APPROVAL,
  DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
  DEFAULT_SHEET_CONFERENCE_BY,
  DSAttendanceAutoApproval,
  DSAttendanceSheetConferenceBy
} from '@/constants/ds-attendance-auto-approval.constants'
import {
  DSAttendanceUploadResponse,
  DSAttendanceContainerListResponse,
  DSAttendanceContainerDetailResponse,
  DSAttendanceAuditListResponse,
  DSAttendancePolicyDetailResponse,
  DSAttendanceCommentListResponse,
  DSAttendanceCommentItem,
  DSAttendanceConciliateResponse,
  DSAttendanceConfirmConciliationResponse,
  DSAttendanceDoctorSuggestionsResponse,
  DSAttendancePrefillResponse,
  DSAttendanceSheetConferenceResponse,
  DSAttendanceMarkAbsentResponse,
  DSAttendanceUnmarkAbsentResponse,
  DSAttendanceBulkApproveResponse,
  DSAttendanceExpectedSummaryItem,
  DSAttendanceExpectedSummaryResponse,
  DSAttendanceNextContainerResponse
} from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import type {
  DSAttendanceDeleteContainerPayload,
  DSAttendanceCreateCommentPayload,
  DSAttendanceConciliatePayload,
  DSAttendanceExportContainersParams,
  DSAttendanceApprovePayload,
  DSAttendanceLegOverwriteDecision,
  DSAttendanceRejectPayload,
  DSAttendanceConfirmConciliationPayload,
  DSAttendanceStartAuditPayload,
  DSAttendanceDoctorSuggestionsParams,
  DSAttendancePrefillPayload,
  DSAttendanceConfirmSheetConferencePayload,
  DSAttendanceMarkAbsentPayload,
  DSAttendanceBulkApprovePayload,
  ScheduleShiftsDto,
  NonDailyScheduleShiftsDto,
  DSAttendanceExpectedSummaryParams,
  DSAttendanceUploadExtras
} from '@/Network/Types/Requests/DsAttendanceRequest'
import type {
  DoctorStationRecordsResponse,
  LegOverwritePreview
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'

/**
 * O timeout global é de 120s justamente por causa dos uploads de folha (parse +
 * validação + insert em lote no backend). Quando ele estoura, a mensagem genérica
 * de timeout não diz ao usuário que arquivos grandes levam minutos nem o que fazer.
 */
const ACTIVITY_REQUESTS_XLSX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const UPLOAD_TIMEOUT_MESSAGE =
  'O envio da folha de ponto demorou mais que o esperado. Verifique sua conexão e tente novamente — arquivos grandes podem levar alguns minutos.'

/**
 * ESP-02/29-04 — rotas do espelho da escala em UM único lugar de propósito: um ajuste de
 * nomenclatura se resolve aqui, sem tocar em nenhuma tela. Conferidas contra o
 * `29-04-SUMMARY.md` do backend (03/09), que é o contrato real.
 *
 * `mark-absent` leva `containerId` na query, e não é opcional: o `PermissionsGuard` resolve
 * o recurso do SpiceDB a partir de um id de CONTAINER (uma rota só com `entryId` não tem
 * como ser autorizada) e o service busca a entry pela chave composta
 * `{ id, containerId }` — entry de outro registro devolve 404 em vez de vazar (IDOR). Mesmo
 * precedente já em produção do `PATCH /operations/entries/:entryId?containerId=`.
 */
const PREFILL_ROUTES = {
  prefill: (containerId: number | string) =>
    `/ds-attendance/operations/containers/${containerId}/prefill`,
  confirmSheetConference: (containerId: number | string) =>
    `/ds-attendance/operations/containers/${containerId}/confirm-sheet-conference`,
  markAbsent: (entryId: number | string, containerId: number | string) =>
    `/ds-attendance/operations/entries/${entryId}/mark-absent?containerId=${containerId}`,
  unmarkAbsent: (entryId: number | string, containerId: number | string) =>
    `/ds-attendance/operations/entries/${entryId}/unmark-absent?containerId=${containerId}`
} as const

/**
 * F3 (§1.5) — os três prazos da solicitação automática como a política pode devolvê-los.
 * Declarados aqui, e não em `DSAttendancePolicyDetailResponse`, porque o contrato do
 * backend ainda está sendo construído: quando ele estabilizar, isto sobe para o arquivo de
 * respostas sem tocar em nenhuma tela. O `settings` opcional repete o padrão da seção
 * "Auditoria" — o web não pode depender de qual lado deploya primeiro.
 */
interface PolicyActivityRequestFields {
  requestAfterCheckoutHoursApp?: number | null
  requestAfterDaysSheet?: number | null
  requestAfterHoursExternal?: number | null
  activityRequestSendWindowHoursApp?: number | null
  activityRequestSendWindowDaysSheet?: number | null
  settings?: {
    requestAfterCheckoutHoursApp?: number | null
    requestAfterDaysSheet?: number | null
    requestAfterHoursExternal?: number | null
    activityRequestSendWindowHoursApp?: number | null
    activityRequestSendWindowDaysSheet?: number | null
  } | null
}

type PolicyActivityRequestField = Exclude<keyof PolicyActivityRequestFields, 'settings'>

interface DSAttendanceByCompanyResponse {
  data: DSAttendanceContainer[]
  count: number
  summary: Record<DSAttendanceStatus, number>
}

/* ------------------------------------------------------------------------- *
 * F2 — Registro de Atividade: contratos das rotas novas.
 * ------------------------------------------------------------------------- */

/**
 * 404/405/501 = a rota ainda não foi deployada no backend. O web deploya antes do
 * backend em toda a F2, então cada rota nova precisa de um default seguro em vez
 * de um toast — mesmo padrão já em produção no "próximo da fila"
 * (`getNextContainerId` do espelho da escala, F0).
 */
const BACKEND_WITHOUT_ROUTE_STATUSES = [404, 405, 501]

/**
 * F4 — recorte da exportação de ANEXOS de Registro de Atividade.
 *
 * Estende o recorte do export de registros porque a DTO do backend faz o mesmo
 * (`DSAttendanceAttachmentExportQueryDto extends DSAttendanceExportQueryDto`).
 * `kind` está fora de propósito: o servidor o força para `ATIVIDADE`.
 *
 * Declarado aqui, e não em `DsAttendanceRequest.ts`, porque aquele arquivo não
 * está no recorte desta task.
 */
export type DSAttendanceAttachmentExportParams = Omit<
  DSAttendanceExportContainersParams,
  'kind' | 'withSummaryPage'
> & {
  /** Partes do nome de cada anexo, NA ORDEM. Vazio = default das quatro. */
  nameBy?: ExportNamePart[]
}

/** `GET .../attachments/export/estimate` — o que o modal mostra antes do clique. */
export interface DSAttendanceAttachmentExportEstimate {
  containers: number
  attachments: number
  /**
   * Soma de `file_size_kb × 1024`, só dos anexos COM tamanho medido; é
   * ESTIMATIVA, o ZIP comprime. Anexos migrados do legado têm `file_size_kb = 0`
   * e não entram nesta soma — ver `attachmentsWithoutSize`/`estimateComplete`.
   */
  estimatedBytes: number
  /**
   * F4-FX3 — quantos anexos do recorte não têm tamanho conhecido (legado).
   * Campo ADITIVO: backend antigo não manda, e `undefined` deve se comportar
   * como hoje (estimativa completa).
   */
  attachmentsWithoutSize?: number
  /**
   * F4-FX3 — `false` quando `attachmentsWithoutSize > 0`; `estimatedBytes`
   * cobre só uma PARTE do recorte. `undefined` (backend antigo) mantém o
   * texto de hoje.
   */
  estimateComplete?: boolean
}

/**
 * Default seguro de `registration-settings`: com ele a tela renderiza EXATAMENTE
 * como hoje (nada bloqueado, Registro de Atividade invisível).
 */
export const DEFAULT_REGISTRATION_SETTINGS: Readonly<RegistrationSettings> = Object.freeze({
  manualCheckBlockEnabled: false,
  activityRecordsEnabled: false,
  acceptedActivityTypes: [],
  checkinLateToleranceMinutes: null,
  checkoutEarlyToleranceMinutes: null,
  monitoringType: null,
  hasSheetRuleInEffect: null,
  isBackendAvailable: false
})

/**
 * Cópia PROFUNDA do default. `DEFAULT_REGISTRATION_SETTINGS` é uma constante de
 * módulo: devolvê-la por referência (ou um spread raso, que compartilha o array)
 * deixaria qualquer mutação de `store.registrationSettings.*` envenenar o default
 * pelo resto da sessão.
 */
export const createDefaultRegistrationSettings = (): RegistrationSettings => ({
  ...DEFAULT_REGISTRATION_SETTINGS,
  acceptedActivityTypes: [...DEFAULT_REGISTRATION_SETTINGS.acceptedActivityTypes]
})

/** Config de registro servida pelo backend: flag global + política da escala. */
export interface RegistrationSettings {
  /** Flag GLOBAL `DS_MANUAL_CHECK_BLOCK_ENABLED` (Q4/Q18). */
  manualCheckBlockEnabled: boolean
  /** `activity_records_enabled` da política da escala consultada. */
  activityRecordsEnabled: boolean
  acceptedActivityTypes: ActivityType[]
  checkinLateToleranceMinutes: number | null
  checkoutEarlyToleranceMinutes: number | null
  /**
   * Modalidade da política de registro VIGENTE da escala consultada. `null` quando
   * não há política/regra vigente, quando a consulta não tem escala no escopo, ou
   * quando o backend ainda não serve o campo — nos três casos a tela não deduz nada.
   */
  monitoringType?: DSAttendanceMonitoringType | null
  /**
   * Existe regra de FOLHA vigente para a escala na data de referência. `null` é
   * "o backend não respondeu isto", e não "não existe": é o que separa degradar
   * de bloquear.
   */
  hasSheetRuleInEffect?: boolean | null
  /**
   * NÃO vem do backend: é metadado do cliente, preenchido por
   * `getRegistrationSettings`. `true` só quando a rota respondeu 200 **com**
   * `activityRecordsEnabled` — o sinal de "backend da F2 presente" (feature
   * detection). 404/405/501, body vazio ou um 200 de backend anterior à F2
   * devolvem `false`, e a tela fica exatamente a de hoje.
   *
   * Opcional no tipo para que um objeto montado à mão (testes, mocks de outras
   * telas) continue válido; ausente é lido como "backend não detectado".
   */
  isBackendAvailable?: boolean
}

/**
 * Aviso não-bloqueante devolvido pelo envio (ex.: horário fora da tolerância).
 * `doctorStationId` é obrigatório no contrato final (B17 §1.1): é ele que liga o
 * aviso à linha do passo 6. `code` fica aberto de propósito — um código novo do
 * backend precisa aparecer com a mensagem dele, não sumir da tela.
 */
export interface ActivityRecordWarning {
  code: ActivityRecordWarningCode | string
  message: string
  doctorStationId: number
}

export type ActivityRecordWarningCode = 'POST_STATION_CLOSED' | 'ALREADY_OK' | 'OUT_OF_TOLERANCE'

/** Sinais de risco calculados no envio e repetidos no card da fila (§3.1). */
export interface ActivityRiskFlagsResponse {
  outOfTolerance: boolean
  otherType: boolean
  submitterRecentlyRejected: boolean
}

/**
 * Um container criado pelo envio — um por plantão do pacote (B17 §1.1).
 *
 * O backend manda `id` E `containerId` com o MESMO valor (B17 §2.3): `id` é o nome
 * do contrato final e `containerId` o alias mantido para não quebrar o cliente
 * antigo. O web lê `id` e trata `containerId` como fallback, para que o dia em que
 * o alias sair não seja um incidente.
 */
export interface ActivityRecordCreatedContainer {
  id: number
  /** Alias de `id`. Só existe enquanto o backend mantiver os dois nomes. */
  containerId?: number
  doctorStationId: number
  status: DSAttendanceStatus | string
  /** 0..3 — a tela NÃO exibe o número (§13), só os chips de `activityRiskFlags`. */
  activityRiskScore?: number | null
  activityRiskFlags?: Partial<ActivityRiskFlagsResponse> | null
}

/** Resposta do `POST operations/activity-records` — N containers, um por plantão. */
export interface ActivityRecordCreatedResponse {
  containers: ActivityRecordCreatedContainer[]
  /** Um id por ANEXO, compartilhado entre os N containers do mesmo envio. */
  evidenceIds: number[]
  warnings: ActivityRecordWarning[]
}

/** A fonte de uma perna do efetivo, como o prefill e a prévia a descrevem. */
export interface EffectiveSourceRef {
  kind: EffectiveSourceKind | string
  method?: string | null
}

/** O efetivo de hoje do plantão. `null` quando o plantão ainda não tem registro nenhum. */
export interface ActivityRecordPrefillEffective {
  checkin: string | null
  checkout: string | null
  checkinSource: EffectiveSourceRef | null
  checkoutSource: EffectiveSourceRef | null
  registrationStatus: EffectiveRegistrationStatus | string
  /** Pós-plantão já aprovado: o que o emissor mandar não vai valer (M3-3). */
  frozen: boolean
}

/**
 * Uma linha do "o que o sistema já tem" (§2.4), por plantão selecionado.
 *
 * Shape final do B17 §1.1: o efetivo vem ANINHADO (`effective`, `null` quando não há
 * registro) e a tolerância vem dentro de `policy` — o achatamento `effectiveCheckin`/
 * `startHour` que a W1 supôs nunca existiu no backend. Os nomes podem vir `null`, e é
 * com o PREVISTO (`expectedStart`/`expectedEnd`) que o formulário abre (Q5).
 */
export interface ActivityRecordPrefillItem {
  doctorStationId: number
  doctorName: string | null
  scheduleName: string | null
  healthUnitName: string | null
  expectedStart: string | null
  expectedEnd: string | null
  /** Fuso da unidade (IANA). É o fuso de parede do plantão, nunca o do navegador. */
  timezone?: string | null
  effective: ActivityRecordPrefillEffective | null
  suggestedCoveredLegs: CoveredLegs | string
  policy: {
    acceptedActivityTypes: ActivityType[] | null
    checkinLateToleranceMinutes: number | null
    checkoutEarlyToleranceMinutes: number | null
    activityRecordsEnabled: boolean
  }
}

/**
 * Um plantão candidato a "Adicionar outros plantões" (BF16, Q15) — passo 2 do
 * wizard. Vem SEMPRE do MESMO médico da vaga de origem, dentro da janela de
 * `days`; substitui o `SendRecordContext.otherStations` que nunca foi
 * populado (WF9/WF10) — a rota é quem sabe quais plantões existem, o modal só
 * busca.
 */
export interface ActivityRecordCandidateStation {
  doctorStationId: number
  date: string
  scheduleId: number
  scheduleName: string | null
  healthUnitId: number
  healthUnitName: string | null
  expectedStart: string | null
  expectedEnd: string | null
  /** Fuso da escala/unidade (`-03:00`) — parede do plantão, nunca o do navegador. */
  timezone: string
  /** Aceita `BOTH|CHECKIN|CHECKOUT|NONE` (contrato BF16); `NONE` não existe em `CoveredLegs`. */
  suggestedCoveredLegs: CoveredLegs | string
  registrationStatus: EffectiveRegistrationStatus | string
  hasOpenActivityRecord: boolean
}

interface ActivityRecordCandidatesResponse {
  doctorId: number
  originDoctorStationId: number
  days: number
  stations: ActivityRecordCandidateStation[]
}

/**
 * Contagem da fila por espécie de registro, para o segmented control de M3.
 *
 * Shape final do B17 §1.1 (rota 4): `total` — e não o `all` que a W1 supôs — mais o
 * `requests` da F3, que na F2 vem sempre `0`. A contagem é SEMPRE das três espécies
 * sobre os mesmos filtros: o backend ignora o recorte por `kind` da própria query,
 * porque contar só a aba aberta zeraria as outras.
 */
export interface ContainerCountsByKind {
  total: number
  folha: number
  atividade: number
  /**
   * F3 — solicitações automáticas em aberto. Campo do contrato final (B17 §1.1): na F2 vem
   * sempre `0` e a aba "Solicitações" fica oculta; quando a F3 devolve número, a aba nasce.
   * O backend da F3 conta este campo IGNORANDO o filtro de status (T8b).
   */
  requests: number
}

/** Perna do plantão, como o backend a nomeia no `changedLegs` e no `winsLeg`. */
export type EffectivePreviewLegKey = 'CHECKIN' | 'CHECKOUT'

/**
 * Uma perna dentro de `items[]`. A fonte vem como OBJETO e com o `label` já resolvido no
 * servidor (B17 §1.1 rota 5) — a tela não monta rótulo de fonte.
 */
export interface EffectivePreviewLeg {
  value: string | null
  source: {
    kind: EffectiveSourceKind | string
    method?: string | null
    label?: string | null
  } | null
  wouldWin: boolean
}

/** Simulação do efetivo com o valor que o auditor está prestes a gravar (M3). */
export interface EffectivePreview {
  doctorStationId: number
  checkin: EffectivePreviewLeg
  checkout: EffectivePreviewLeg
}

/** Um lado da comparação antes → depois. As pernas vêm achatadas (`§3.4`). */
export interface EffectivePreviewSide {
  checkin: string | null
  checkout: string | null
  checkinSource: EffectiveSourceKind | string | null
  checkoutSource: EffectiveSourceKind | string | null
  registrationStatus: EffectiveRegistrationStatus | string
}

/** Uma posição do ranking de fontes, com o rótulo já resolvido no servidor. */
export interface EffectivePreviewPrecedenceItem {
  rank: number
  kind: EffectiveSourceKind | string
  label: string
  winsLeg: EffectivePreviewLegKey[]
}

/**
 * Envelope de `containers/:id/effective-preview` (B17 §1.1 rota 5).
 *
 * É ele que a tela de auditoria consome INTEIRO: `precedence` e `registrationStatus` vêm
 * prontos do backend e a comparação antes → depois sai de `before`/`after`/`changedLegs`.
 * Devolver só `items` — o que a W1 fazia — jogava o envelope fora e obrigava a tela a
 * recalcular ranking e estado no cliente, criando uma segunda fonte de verdade.
 */
export interface EffectivePreviewEnvelope {
  /** Um item: o plantão do container. */
  items: EffectivePreview[]
  precedence: EffectivePreviewPrecedenceItem[]
  /** O estado do plantão DEPOIS (é o `registrationStatus` do `after`). */
  registrationStatus: EffectiveRegistrationStatus | string
  before: EffectivePreviewSide | null
  after: EffectivePreviewSide
  changedLegs: EffectivePreviewLegKey[]
  /** Pós-plantão já aprovado: o `after` não vai valer (M3-3). */
  frozen: boolean
  afterClosure: boolean
}

/**
 * Opção do combo "Unidades" da fila unificada.
 *
 * Vem de `GET ds-attendance/operations/filters/health-units`, e NÃO de
 * `/healthUnits/list-view`: quem só tem papel de ds_attendance (receptor puro) não é leitor de
 * unidade e tomava 403 lá, que a store transformava em lista vazia sem aviso. O shape espelha o
 * `HealthUnitSelectViewResponse` de propósito, para o mapeamento da tela não mudar.
 */
export interface DSAttendanceHealthUnitFilterOption {
  value: number
  title: string
  subtitle: string | null
  companyId: number | null
  operationalGroup: string | null
}

/**
 * Opção do combo "Escala" da fila unificada.
 *
 * `healthUnitId` é o que sustenta a cascata unidade -> escala em memória: sem ele, selecionar uma
 * unidade obrigaria a refazer a busca no `GET /schedules`, que é o outro endpoint que responde 403
 * para o receptor puro.
 */
export interface DSAttendanceScheduleFilterOption {
  value: number
  title: string
  subtitle: string | null
  healthUnitId: number
  companyId: number | null
}

export default class DSAttendanceService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/ds-attendance/policies'
  }

  async getPolicyById(
    id: number | string,
    scheduleId?: number | string
  ): Promise<DSAttendancePolicyData> {
    try {
      const params = scheduleId ? `?scheduleId=${scheduleId}` : ''
      const response = (await request.get(`${this.getCompletePath()}/${id}${params}`)) as {
        data: DSAttendancePolicyDetailResponse
      }
      return this.mapPolicyDetail(response.data)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao buscar detalhes da política.')
    }
  }

  async getActiveModalities(
    healthUnitId: number | string,
    referenceDate: string
  ): Promise<ActiveModality[]> {
    try {
      const query = new URLSearchParams({
        healthUnitId: String(healthUnitId),
        referenceDate
      })
      const response = (await request.get(
        `${this.getCompletePath()}/active-modalities?${query.toString()}`
      )) as { data: ActiveModality[] }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao listar modalidades de registro.')
    }
  }

  async listPolicies(
    scheduleId: number | string,
    referenceDate?: string
  ): Promise<DSAttendancePolicyData[]> {
    try {
      const query = new URLSearchParams({ scheduleId: String(scheduleId) })
      if (referenceDate) {
        query.set('referenceDate', referenceDate)
      }
      const response = (await request.get(`${this.getCompletePath()}?${query.toString()}`)) as {
        data: DSAttendancePolicyDetailResponse[]
      }
      // A listagem devolve a política crua (sem `mapPolicyDetail`), mas os prazos da
      // solicitação automática (F3) podem vir dentro de `settings`. Normalizar aqui evita
      // que a aba de políticas mostre "usar o default" para um canal que está desligado.
      return response.data.map((policy) => ({
        id: policy.id,
        name: policy.name,
        isActive: policy.isActive,
        vigencyStatus: policy.vigencyStatus,
        createdBy: policy.createdBy ?? null,
        isInEffect: policy.isInEffect,
        monitoringType: (policy.monitoringType ??
          DSAttendanceMonitoringType.MINHA_ESCALA_APP) as DSAttendanceMonitoringType,
        externalApp: policy.externalApp ?? null,
        requiredCheckOurTeam: policy.requiredCheckOurTeam ?? null,
        requiredCheckNotOurTeam: policy.requiredCheckNotOurTeam ?? null,
        checkMethodOurTeam: (policy.checkMethodOurTeam as CheckMethod) ?? null,
        checkMethodNotOurTeam: (policy.checkMethodNotOurTeam as CheckMethod) ?? null,
        ...this.mapPolicyPermanence(policy),
        ...this.mapPolicyTolerances(policy),
        ...this.mapPolicyAudit(policy),
        ...this.mapPolicyActivityRequest(policy),
        ...this.mapPolicyActivityRecords(policy),
        rules: this.mapPolicyRules(policy.rules),
        links: this.mapPolicyLinks(policy.links),
        templates: this.mapPolicyTemplates(policy.templates)
      }))
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao listar políticas de presença.')
    }
  }

  /**
   * Buscar os detalhes de um container de presença
   * Corresponde a: GET /ds-attendance/operations/containers/:id
   */
  async getById(id: number | string): Promise<DSAttendanceHybridDetail> {
    try {
      // 1. Busca o Container (Operação)
      const response = (await request.get(`/ds-attendance/operations/containers/${id}`)) as {
        data: DSAttendanceContainerDetailResponse
      }

      if (response.data.sheetTemplates) {
        response.data.sheetTemplates = response.data.sheetTemplates.map((tpl) => ({
          ...tpl,
          columns: (tpl.columns ?? []).map((col: unknown) =>
            typeof col === 'string'
              ? col
              : ((col as Record<string, string>)?.name ??
                (col as Record<string, string>)?.label ??
                String(col))
          )
        }))
      }

      // 2. Mapeia a parte da Política (apenas se o container contém campos de política).
      // Containers ATIVIDADE não têm política, apenas OPERAÇÃO têm.
      const mappedPolicy = this.validatePolicyFieldsOnContainer(response.data)
        ? this.mapPolicyDetail(
            response.data as DSAttendanceContainerDetailResponse & DSAttendancePolicyDetailResponse
          )
        : { rules: [], templates: [], links: [] }

      // 3. RETORNO HÍBRIDO: Faz o merge dos dois objetos.
      // Isso garante que o 'status', 'referenceDate' e outros campos do Container
      // continuem existam no objeto final junto com as 'rules' da Política (se presentes).
      return {
        ...response.data,
        ...mappedPolicy
      } as DSAttendanceHybridDetail
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao buscar detalhes completos do registro.')
    }
  }

  /**
   * Regra de serialização dos escalares: `undefined`/`null`/`false`/`''` NUNCA sobe;
   * `true` vira `'true'`; o resto vira `String(value)`. `false` fica de fora porque
   * `overdue=false` ou `hasDivergentEntries=false` diriam ao backend algo diferente de
   * "não filtrar".
   */
  private appendScalarParams(
    query: URLSearchParams,
    scalarParams: Record<string, string | number | boolean | null | undefined>
  ): void {
    for (const [key, value] of Object.entries(scalarParams)) {
      if (value === undefined || value === null || value === false || value === '') continue
      query.append(key, value === true ? 'true' : String(value))
    }
  }

  /**
   * O ÚNICO montador dos filtros de RECORTE da fila. É compartilhado por `by-company`,
   * `containers/:id/next` e pelas três exportações (registros, anexos e estimativa):
   * filtro novo entra aqui uma vez e chega a todas as rotas por construção. Antes eram
   * duas montagens independentes, e elas divergiram três vezes (`shiftIds`,
   * `activityType`, `manualReviewReason`) — cada vez um filtro visível na tela que o
   * arquivo exportado ignorava.
   *
   * Fora daqui ficam só o que é próprio de cada rota: período/paginação/ordenação da
   * listagem e `dateType`/`format`/`withSummaryPage`/`nameBy` das exportações.
   */
  private appendContainerFilters(
    query: URLSearchParams,
    params: DSAttendanceContainerFilterParams
  ): void {
    // Arrays sobem como parâmetro REPETIDO (`status=A&status=B`); vazio não entra.
    params.healthUnitIds?.forEach((id) => query.append('healthUnitIds', String(id)))
    params.scheduleIds?.forEach((id) => query.append('scheduleIds', String(id)))
    params.shiftIds?.forEach((id) => query.append('shiftIds', String(id)))
    params.status?.forEach((s) => query.append('status', s))
    // Fase 2 (§6.3) — filtro "Motivo da revisão": array repetido, como `status`.
    params.manualReviewReason?.forEach((reason) => query.append('manualReviewReason', reason))
    // "Modalidade de registro" (acréscimo do dono): repetido, como `status`.
    params.registrationModes?.forEach((mode) => query.append('registrationModes', mode))

    this.appendScalarParams(query, {
      // "Data upload" da fila é o upload REAL da evidência. O par
      // `expectedSubmissionAt*` continua no contrato do backend (é o PRAZO da
      // política), mas a fila não o usa mais: era ele que fazia o filtro de um
      // dia devolver registros exibindo outro.
      uploadStartDate: params.uploadStartDate,
      uploadEndDate: params.uploadEndDate,
      overdue: params.overdue,
      hasDivergentEntries: params.hasDivergentEntries,
      hasAntecipe: params.hasAntecipe,
      hasInCash: params.hasInCash,
      stationChangedAfterAudit: params.stationChangedAfterAudit,
      // Fase 1 do fluxo de auditoria: só sobe quando ligado — o backend já esconde os
      // registros sem escala prevista por padrão, e `=false` não é "não filtrar".
      includeNoScheduleExpected: params.includeNoScheduleExpected,
      // F2 — fila unificada: `kind` ausente significa "Todos", nunca `FOLHA`.
      // `doctorStationId` é o que a aba de Registros de Atividade da vaga usa.
      kind: params.kind,
      activityType: params.activityType,
      // F3 — recorte da aba "Solicitações". Booleano que só sobe quando ligado, como os
      // demais toggles acima.
      onlyRequests: params.onlyRequests,
      doctorStationId: params.doctorStationId
    })
  }

  /**
   * Query-string do `by-company`, extraída para ser reaproveitada pelo "próximo da fila"
   * (`containers/:id/next`), que recebe exatamente os mesmos filtros — assim o próximo
   * registro respeita a fila que o auditor está olhando, sem uma segunda montagem que possa
   * divergir. Recorte = `appendContainerFilters`; o que sobra é só da listagem.
   */
  private buildContainerListQuery(params: ListContainersByCompanyParams): URLSearchParams {
    const query = new URLSearchParams()

    this.appendContainerFilters(query, params)
    this.appendScalarParams(query, {
      startDate: params.startDate,
      endDate: params.endDate,
      orderBy: params.orderBy,
      page: params.page,
      limit: params.limit
    })

    return query
  }

  /**
   * Eixo 4 da discovery — próximo registro da fila com os MESMOS filtros da listagem, para o
   * auditor não voltar à lista entre um registro e outro. `nextId` nulo = a fila acabou.
   */
  async getNextContainerId(
    containerId: number | string,
    params: ListContainersByCompanyParams
  ): Promise<number | null> {
    try {
      const query = this.buildContainerListQuery(params)
      const response = (await request.get(
        `/ds-attendance/operations/containers/${containerId}/next?${query.toString()}`
      )) as { data: DSAttendanceNextContainerResponse }
      return response.data?.nextId ?? null
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível buscar o próximo registro da fila.')
    }
  }

  /**
   * Eixo 2A da discovery — "escala prevista" ANTES do envio, para o checklist do emissor.
   * Consultado no fluxo de upload, quando escala e data já estão escolhidas.
   */
  async getExpectedSummary(
    params: DSAttendanceExpectedSummaryParams
  ): Promise<DSAttendanceExpectedSummaryItem[]> {
    try {
      const query = new URLSearchParams({
        healthUnitId: String(params.healthUnitId),
        referenceDate: params.referenceDate
      })
      params.scheduleIds.forEach((id) => query.append('scheduleIds', String(id)))

      /**
       * O backend devolve `{ referenceDate, schedules: [...] }` — nunca um array na raiz.
       * O `Array.isArray` fica como rede de segurança para uma resposta fora do contrato:
       * o checklist do emissor não pode quebrar por causa de uma consulta auxiliar.
       */
      const response = (await request.get(
        `/ds-attendance/operations/expected-summary?${query.toString()}`
      )) as { data: DSAttendanceExpectedSummaryResponse | DSAttendanceExpectedSummaryItem[] }
      const payload = response.data
      if (Array.isArray(payload)) return payload
      return payload?.schedules ?? []
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível consultar a escala prevista para esta data.')
    }
  }

  async listByCompany(
    params: ListContainersByCompanyParams
  ): Promise<DSAttendanceByCompanyResponse> {
    try {
      const query = this.buildContainerListQuery(params)

      const response = (await request.get(
        `/ds-attendance/operations/containers/by-company?${query.toString()}`
      )) as { data: DSAttendanceByCompanyResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao listar os registros de presença por empresa.')
    }
  }

  /**
   * Combos da fila, servidos pelo PRÓPRIO ds-attendance e escopados pelo alcance da pessoa
   * (`ds_attendance#attendance_emitting`, o mesmo par de `containers/by-company`). Erro NÃO é
   * engolido: um 403 aqui significa "não alcança escala nenhuma", e a tela precisa dizer isso em
   * vez de mostrar um combo vazio indistinguível de "a empresa não tem unidades".
   */
  async listFilterHealthUnits(): Promise<DSAttendanceHealthUnitFilterOption[]> {
    try {
      const response = (await request.get('/ds-attendance/operations/filters/health-units')) as {
        data: DSAttendanceHealthUnitFilterOption[]
      }
      return response.data ?? []
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao carregar as unidades do filtro.')
    }
  }

  async listFilterSchedules(): Promise<DSAttendanceScheduleFilterOption[]> {
    try {
      const response = (await request.get('/ds-attendance/operations/filters/schedules')) as {
        data: DSAttendanceScheduleFilterOption[]
      }
      return response.data ?? []
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao carregar as escalas do filtro.')
    }
  }

  private mapPolicyDetail(raw: DSAttendancePolicyDetailResponse): DSAttendancePolicyData {
    return {
      id: raw.id,
      name: raw.name,
      isActive: raw.isActive,
      monitoringType: (raw.monitoringType ??
        DSAttendanceMonitoringType.MINHA_ESCALA_APP) as DSAttendanceMonitoringType,
      externalApp: raw.externalApp ?? null,
      requiredCheckOurTeam: raw.requiredCheckOurTeam ?? null,
      requiredCheckNotOurTeam: raw.requiredCheckNotOurTeam ?? null,
      checkMethodOurTeam: (raw.checkMethodOurTeam as CheckMethod) ?? null,
      checkMethodNotOurTeam: (raw.checkMethodNotOurTeam as CheckMethod) ?? null,
      ...this.mapPolicyTolerances(raw),
      ...this.mapPolicyPermanence(raw),
      ...this.mapPolicyAudit(raw),
      ...this.mapPolicyActivityRequest(raw),
      ...this.mapPolicyActivityRecords(raw),
      rules: this.mapPolicyRules(raw.rules),
      links: this.mapPolicyLinks(raw.links),
      templates: this.mapPolicyTemplates(raw.templates)
    }
  }

  private mapPolicyTolerances(raw: DSAttendancePolicyDetailResponse) {
    return {
      checkinLateToleranceMinutes: raw.checkinLateToleranceMinutes ?? null,
      checkoutEarlyToleranceMinutes: raw.checkoutEarlyToleranceMinutes ?? null,
      submissionDeadlineHours: raw.submissionDeadlineHours ?? null,
      deadlineCalculationType: (raw.deadlineCalculationType ??
        null) as DSAttendanceDeadlineCalculationType | null
    }
  }

  /**
   * Seção "Auditoria" (spec 2026-09-11, §6.1). Aceita os campos no topo OU dentro de
   * `settings`: o contrato do backend está sendo construído em paralelo, e a tela não pode
   * depender de qual das duas formas o deploy trouxer primeiro. Ausentes, caem nos padrões.
   */
  private mapPolicyAudit(raw: DSAttendancePolicyDetailResponse) {
    const settings = raw.settings ?? null
    return {
      sheetConferenceBy: (raw.sheetConferenceBy ??
        settings?.sheetConferenceBy ??
        DEFAULT_SHEET_CONFERENCE_BY) as DSAttendanceSheetConferenceBy,
      autoApproval: (raw.autoApproval ??
        settings?.autoApproval ??
        DEFAULT_AUTO_APPROVAL) as DSAttendanceAutoApproval,
      manualReviewSampleRate:
        raw.manualReviewSampleRate ??
        settings?.manualReviewSampleRate ??
        DEFAULT_MANUAL_REVIEW_SAMPLE_RATE
    }
  }

  /**
   * F3 (spec 2026-09-12, §1.5/§6.2) — prazos da solicitação automática. Sem esta leitura o
   * formulário da política abriria sempre vazio e o primeiro save apagaria o que estava
   * configurado.
   *
   * `null` é preservado (significa "usar o default do produto") e `0` também (significa
   * "canal desligado"): por isso `??` e nunca `||`, que trataria `0` como ausente. Aceita os
   * campos no topo OU dentro de `settings` pelo mesmo motivo de `mapPolicyAudit`.
   */
  private mapPolicyActivityRequest(raw: DSAttendancePolicyDetailResponse) {
    const source = raw as DSAttendancePolicyDetailResponse & PolicyActivityRequestFields
    const settings = source.settings ?? null
    const pick = (field: PolicyActivityRequestField): number | null =>
      source[field] ?? settings?.[field] ?? null
    return {
      requestAfterCheckoutHoursApp: pick('requestAfterCheckoutHoursApp'),
      requestAfterDaysSheet: pick('requestAfterDaysSheet'),
      requestAfterHoursExternal: pick('requestAfterHoursExternal'),
      activityRequestSendWindowHoursApp: pick('activityRequestSendWindowHoursApp'),
      activityRequestSendWindowDaysSheet: pick('activityRequestSendWindowDaysSheet')
    }
  }

  /**
   * Seção "Registro de Atividade · Jornada" (§1.5) mais o `postStationBlockOnRegistration`
   * da F4. Cópia CRUA e tolerante, sem default: quem escolhe o padrão é o formulário
   * (`loadActivityRecordFields`), e ele distingue "backend antigo não mandou o campo"
   * (`undefined`) de "o backend disse que está desligado" (`false`).
   *
   * Sem isto, o detalhe entregava `undefined` para uma política LIGADA, o switch abria
   * desligado e qualquer salvamento devolvia `activityRecordsEnabled: false` ao backend,
   * desligando a feature da escala sem ninguém pedir.
   */
  private mapPolicyActivityRecords(raw: DSAttendancePolicyDetailResponse) {
    return {
      activityRecordsEnabled: raw.activityRecordsEnabled,
      acceptedActivityTypes: this.validateActivityTypes(raw.acceptedActivityTypes),
      sourcePrecedence: this.validateSourceKinds(raw.sourcePrecedence),
      postStationBlockOnRegistration: raw.postStationBlockOnRegistration
    }
  }

  private validateActivityTypes(types: unknown): ActivityType[] | null | undefined {
    if (types === null || types === undefined) return types
    if (!Array.isArray(types)) return undefined
    const validTypes = Object.values(ActivityType)
    const filtered = types.filter((t) => validTypes.includes(t as ActivityType))
    return filtered.length > 0 ? (filtered as ActivityType[]) : undefined
  }

  private validateSourceKinds(kinds: unknown): EffectiveSourceKind[] | null | undefined {
    if (kinds === null || kinds === undefined) return kinds
    if (!Array.isArray(kinds)) return undefined
    const validKinds = Object.values(EffectiveSourceKind)
    const filtered = kinds.filter((k) => validKinds.includes(k as EffectiveSourceKind))
    return filtered.length > 0 ? (filtered as EffectiveSourceKind[]) : undefined
  }

  private validatePolicyFieldsOnContainer(
    container: unknown
  ): container is DSAttendanceContainerDetailResponse &
    Pick<DSAttendancePolicyDetailResponse, 'rules' | 'links' | 'templates'> {
    if (!container || typeof container !== 'object') return false

    const obj = container as Record<string, unknown>

    // Containers do tipo OPERACAO têm política; ATIVIDADE não têm.
    // O discriminante é o `kind` (OPERATION vs ACTIVITY).
    const kind = (obj.kind as string) ?? ''
    return kind === 'OPERATION'
  }

  private mapPolicyPermanence(raw: DSAttendancePolicyDetailResponse) {
    return {
      autoPermanenceOurTeam: raw.autoPermanenceOurTeam ?? true,
      autoPermanenceNotOurTeam: raw.autoPermanenceNotOurTeam ?? true
    }
  }

  private mapPolicyLinks(
    links: DSAttendancePolicyDetailResponse['links']
  ): DSAttendancePolicyData['links'] {
    return (links ?? []).map((link) => ({
      id: link.id,
      startDate: link.startDate ?? null,
      endDate: link.endDate ?? null,
      isActive: link.isActive
    }))
  }

  private mapPolicyTemplates(
    templates: DSAttendancePolicyDetailResponse['templates']
  ): DSAttendancePolicyData['templates'] {
    return (templates ?? []).map((t) => ({
      id: t.id,
      name: t.name ?? '',
      templateFields: t.templateFields ?? { columns: [], rows: 0 }
    }))
  }

  private mapPolicyRules(
    rules: DSAttendancePolicyDetailResponse['rules']
  ): DSAttendancePolicyData['rules'] {
    return (rules ?? []).map((rule) => ({
      name: rule.name ?? '',
      shiftIds: rule.shiftIds ?? [],
      recurrence: (rule.recurrence ?? null) as DSAttendanceRuleRecurrence | null,
      dayOfMonth: rule.dayOfMonth ?? null,
      daysOfWeek: rule.daysOfWeek ?? []
    }))
  }

  /**
   * Lista os containers de presença paginados com filtros server-side
   * Corresponde a: GET /ds-attendance/operations/containers
   */
  async listDSAttendanceContainers(
    params?: Record<string, unknown>
  ): Promise<DSAttendanceContainerListResponse> {
    try {
      const response = (await request.get(`/ds-attendance/operations/containers`, params)) as {
        data: DSAttendanceContainerListResponse
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao listar os registros de presença.')
    }
  }

  /**
   * Corresponde a: @Post() create()
   */
  async createPolicy(payload: CreateDSAttendancePolicyPayload): Promise<DSAttendancePolicyData> {
    try {
      const response = (await request.post(this.getCompletePath(), payload)) as {
        data: DSAttendancePolicyData
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao criar a política.')
    }
  }

  async updatePolicy(
    id: number | string,
    payload: UpdateDSAttendancePolicyPayload,
    scheduleId?: number | string
  ): Promise<DSAttendancePolicyData> {
    try {
      const params = scheduleId ? `?scheduleId=${scheduleId}` : ''
      const response = (await request.patch(
        `${this.getCompletePath()}/${id}${params}`,
        payload
      )) as {
        data: DSAttendancePolicyData
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao atualizar a política.')
    }
  }

  async uploadEvidence(
    file: File,
    schedules: ScheduleShiftsDto[],
    referenceDate: string,
    healthUnitId: number,
    uploadedById: number,
    rotation: number,
    imageState: string,
    extras?: DSAttendanceUploadExtras
  ): Promise<DSAttendanceUploadResponse> {
    return this.uploadEvidenceToPath(
      '/ds-attendance/operations/upload',
      'Não foi possível enviar a folha de ponto do plantão.',
      file,
      schedules,
      referenceDate,
      healthUnitId,
      uploadedById,
      rotation,
      imageState,
      extras
    )
  }

  async uploadNonDailyEvidence(
    file: File,
    schedules: NonDailyScheduleShiftsDto[],
    referenceDate: string,
    healthUnitId: number,
    uploadedById: number,
    rotation: number,
    imageState: string,
    extras?: DSAttendanceUploadExtras
  ): Promise<DSAttendanceUploadResponse> {
    return this.uploadEvidenceToPath(
      '/ds-attendance/operations/upload-non-daily',
      'Não foi possível enviar a folha de ponto não diária.',
      file,
      schedules,
      referenceDate,
      healthUnitId,
      uploadedById,
      rotation,
      imageState,
      extras
    )
  }

  /**
   * Envio de folha compartilhado por diária e não diária. `fallbackMessage` vem de quem
   * chama porque o método serve duas ações diferentes — um fallback único ("Erro ao
   * realizar upload do registro.") não dizia ao usuário QUAL envio falhou.
   */
  private async uploadEvidenceToPath(
    path: string,
    fallbackMessage: string,
    file: File,
    schedules: ScheduleShiftsDto[] | NonDailyScheduleShiftsDto[],
    referenceDate: string,
    healthUnitId: number,
    uploadedById: number,
    rotation: number,
    imageState: string,
    extras?: DSAttendanceUploadExtras
  ): Promise<DSAttendanceUploadResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('referenceDate', referenceDate)
      formData.append('healthUnitId', String(healthUnitId))
      formData.append('uploadedById', String(uploadedById))
      formData.append('rotation', String(rotation))
      formData.append('imageState', imageState)
      formData.append('schedules', JSON.stringify(schedules))
      // Eixo 2A — declaração do checklist e páginas anexadas. Só entram quando a tela passou
      // pelo checklist: ausentes, o envio é byte a byte o de hoje (backend sem os campos).
      if (extras?.selfCheck) formData.append('selfCheck', JSON.stringify(extras.selfCheck))
      if (typeof extras?.expectedPages === 'number') {
        formData.append('expectedPages', String(extras.expectedPages))
      }

      const response = (await request.post(path, formData)) as {
        data: DSAttendanceUploadResponse
      }
      return response.data
    } catch (e: unknown) {
      // Tratado aqui, e não no funil de `getApiErrorMessage`: só o upload sabe que o
      // timeout de 120s pode ser esperado para um arquivo grande.
      if (isTimeoutError(e)) throw new ServiceError(UPLOAD_TIMEOUT_MESSAGE, e)
      throw this.handleError(e, fallbackMessage)
    }
  }

  async uploadExternalEvidence(
    file: File,
    schedules: ScheduleShiftsDto[],
    referenceDate: string,
    healthUnitId: number
  ): Promise<DSAttendanceUploadResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('referenceDate', referenceDate)
      formData.append('healthUnitId', String(healthUnitId))
      formData.append('schedules', JSON.stringify(schedules))

      const response = (await request.post(
        `/ds-attendance/operations/upload-external-app`,
        formData
      )) as {
        data: DSAttendanceUploadResponse
      }
      return response.data
    } catch (e: unknown) {
      if (isTimeoutError(e)) throw new ServiceError(UPLOAD_TIMEOUT_MESSAGE, e)
      throw this.handleError(
        e,
        'Não foi possível enviar o arquivo de registro do aplicativo externo.'
      )
    }
  }

  /**
   * Histórico de eventos do container paginado.
   * Corresponde a: GET /ds-attendance/operations/containers/:id/audit
   */
  async getAuditTrail(
    id: number | string,
    params?: { page?: number; limit?: number }
  ): Promise<DSAttendanceAuditListResponse> {
    try {
      const response = (await request.get(
        `/ds-attendance/operations/containers/${id}/audit`,
        params
      )) as { data: DSAttendanceAuditListResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao buscar o histórico do registro de presença.')
    }
  }

  /**
   * Excluir container com justificativa obrigatória (apenas Aguardando Envio).
   * Corresponde a: POST /ds-attendance/operations/containers/:id/delete
   */
  async deleteContainer(
    id: number | string,
    payload: DSAttendanceDeleteContainerPayload
  ): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/delete`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao excluir o registro de presença.')
    }
  }

  /**
   * Listar comentários de um container paginados.
   * Corresponde a: GET /ds-attendance/operations/containers/:id/comments
   */
  async listComments(
    id: number | string,
    params?: { page?: number; limit?: number }
  ): Promise<DSAttendanceCommentListResponse> {
    try {
      const response = (await request.get(
        `/ds-attendance/operations/containers/${id}/comments`,
        params
      )) as { data: DSAttendanceCommentListResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao carregar os comentários do registro de presença.')
    }
  }

  /**
   * Criar comentário em um container.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/comments
   */
  async createComment(
    id: number | string,
    payload: DSAttendanceCreateCommentPayload
  ): Promise<DSAttendanceCommentItem> {
    try {
      const response = (await request.post(
        `/ds-attendance/operations/containers/${id}/comments`,
        payload
      )) as { data: DSAttendanceCommentItem }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao adicionar o comentário.')
    }
  }

  /**
   * Excluir comentário de um container.
   * Corresponde a: DELETE /ds-attendance/operations/containers/:id/comments/:commentId
   */
  async deleteComment(id: number | string, commentId: number | string): Promise<void> {
    try {
      await request.delete(`/ds-attendance/operations/containers/${id}/comments/${commentId}`)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao excluir o comentário.')
    }
  }

  /**
   * Inicia a conciliação de um container com as linhas preenchidas da folha.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/conciliate
   */
  async conciliate(
    id: number | string,
    payload: DSAttendanceConciliatePayload
  ): Promise<DSAttendanceConciliateResponse> {
    try {
      const response = (await request.post(
        `/ds-attendance/operations/containers/${id}/conciliate`,
        payload
      )) as { data: DSAttendanceConciliateResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao iniciar a conciliação.')
    }
  }

  /**
   * ESP-02/29-04 — pré-preenche a grade com o espelho da escala do turno, disparado quando o
   * operador SELECIONA o modelo de folha. Idempotente: a segunda chamada com o mesmo
   * `templateId` devolve as mesmas linhas sem duplicar entries.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/prefill
   */
  async prefillEntries(
    id: number | string,
    payload: DSAttendancePrefillPayload
  ): Promise<DSAttendancePrefillResponse> {
    try {
      const response = (await request.post(PREFILL_ROUTES.prefill(id), payload)) as {
        data: DSAttendancePrefillResponse
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível pré-preencher a folha com a escala prevista.')
    }
  }

  /**
   * ESP-02/29-04 — o OK global: confirma de uma vez todas as linhas pré-preenchidas ainda sem
   * veredito e grava a conferência da folha única na trilha. Sem `signaturesChecked` o backend
   * devolve 400 — o front só chega aqui com o checkbox marcado.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/confirm-sheet-conference
   */
  async confirmSheetConference(
    id: number | string,
    payload: DSAttendanceConfirmSheetConferencePayload
  ): Promise<DSAttendanceSheetConferenceResponse> {
    try {
      const response = (await request.post(PREFILL_ROUTES.confirmSheetConference(id), payload)) as {
        data: DSAttendanceSheetConferenceResponse
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível confirmar a conferência da folha.')
    }
  }

  /**
   * ESP-02/29-04 — "não consta na folha": o plantão previsto na escala não aparece na folha
   * física. A linha vira DIVERGENT e alimenta a fila de exceções; nunca é apagada.
   * `containerId` é obrigatório (query param) — é o que o `PermissionsGuard` usa para
   * resolver a permissão e o que restringe a busca da entry à chave composta.
   * Corresponde a: POST /ds-attendance/operations/entries/:entryId/mark-absent?containerId=
   */
  async markEntryAbsent(
    entryId: number | string,
    containerId: number | string,
    payload?: DSAttendanceMarkAbsentPayload
  ): Promise<DSAttendanceMarkAbsentResponse> {
    try {
      const response = (await request.post(
        PREFILL_ROUTES.markAbsent(entryId, containerId),
        payload ?? {}
      )) as {
        data: DSAttendanceMarkAbsentResponse
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível marcar a linha como ausente da folha.')
    }
  }

  /**
   * Desfaz o "não consta na folha": a linha volta a PENDING, sem veredito de
   * espelho e com o vínculo do plantão restaurado.
   */
  async unmarkEntryAbsent(
    entryId: number | string,
    containerId: number | string
  ): Promise<DSAttendanceUnmarkAbsentResponse> {
    try {
      const response = (await request.post(
        PREFILL_ROUTES.unmarkAbsent(entryId, containerId),
        {}
      )) as {
        data: DSAttendanceUnmarkAbsentResponse
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível restaurar a linha para a folha.')
    }
  }

  /**
   * Aprovação em lote de containers 100% conciliados (teto de 50 por request). O backend só
   * considera elegíveis os containers cuja policy tem `bulkApproveEnabled` — os inelegíveis
   * voltam em `failed` com o motivo, nunca aprovados por engano.
   * Corresponde a: POST /ds-attendance/operations/containers/bulk-approve
   */
  async bulkApproveContainers(
    payload: DSAttendanceBulkApprovePayload
  ): Promise<DSAttendanceBulkApproveResponse> {
    try {
      const response = (await request.post(
        '/ds-attendance/operations/containers/bulk-approve',
        payload
      )) as { data: DSAttendanceBulkApproveResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível aprovar os registros em lote.')
    }
  }

  /**
   * Sugestões de profissionais do cadastro da empresa do container (RF-03).
   * A empresa vem de `container.companyId` no backend — nenhum filtro de empresa aqui.
   * Corresponde a: GET /ds-attendance/operations/containers/:id/doctor-suggestions
   */
  async getDoctorSuggestions(
    id: number | string,
    params: DSAttendanceDoctorSuggestionsParams
  ): Promise<DSAttendanceDoctorSuggestionsResponse> {
    try {
      const response = (await request.get(
        `/ds-attendance/operations/containers/${id}/doctor-suggestions`,
        params
      )) as { data: DSAttendanceDoctorSuggestionsResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao buscar sugestões de profissionais.')
    }
  }

  /**
   * Exportar registros de presença agrupados por container (setor/unidade).
   * Corresponde a: GET /ds-attendance/operations/containers/export
   *
   * O recorte é o MESMO da fila por construção: `params` deriva de
   * `ListContainersByCompanyParams` e passa pelo mesmo `appendContainerFilters` do
   * `by-company` — o que o usuário vê listado é o que sai no arquivo. Só o período é
   * próprio daqui: `dateType` diz qual data `startDate`/`endDate` recortam.
   *
   * Sem `kind` exporta as duas espécies (comportamento de quem nunca filtrou). Pedir
   * `kind=ATIVIDADE` nesta rota é 400 `ACTIVITY_EXPORT_REQUIRES_EXTRACTING`: anexo de
   * atividade sai por `exportAttachments`, sob `attendance_extracting`.
   *
   * @param params - Recorte da fila + `dateType`/`startDate`/`endDate` (YYYY-MM-DD) +
   *   `withSummaryPage` (só PDF: página de resumo; ignorado para ZIP)
   * @param format - 'zip' (default; um PDF por registro, compactados) | 'pdf' (único
   *   PDF compilado, ordenado por `dateType`)
   * @returns Promise com dados binários (Blob) e headers da resposta (incluindo content-disposition)
   */
  async exportContainers(
    params: DSAttendanceExportContainersParams,
    format?: 'zip' | 'pdf'
  ): Promise<{ data: BlobPart; headers: Record<string, string> }> {
    try {
      const query = this.buildExportQuery(params)

      if (format && format !== 'zip') {
        query.append('format', format)
      }

      if (params.withSummaryPage) {
        query.append('withSummaryPage', 'true')
      }

      // `await` é necessário para a rejeição cair neste catch — sem ele a promise
      // era retornada crua e o consumer recebia o AxiosError direto.
      const response = await request.get<BlobPart>(
        `/ds-attendance/operations/containers/export?${query.toString()}`,
        undefined,
        undefined,
        'blob'
      )
      return response as { data: BlobPart; headers: Record<string, string> }
    } catch (e: unknown) {
      // `responseType: 'blob'` — o body JSON do erro vem dentro de um Blob; sem o
      // unwrap, a explicação do backend nunca chegava ao usuário.
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar os registros de presença.'
      )
    }
  }

  /**
   * Query comum às três exportações (registros, anexos e a estimativa dos anexos):
   * período tipado + o MESMO recorte da fila (`appendContainerFilters`). Estimativa e
   * download PRECISAM montar a mesma query — divergir faria a tela prometer "213
   * anexos" e entregar outra coisa —, e fila e exportação também: um filtro que a
   * lista aplica e o arquivo ignora é um arquivo que não bate com a tela.
   */
  private buildExportQuery(params: DSAttendanceExportContainersParams): URLSearchParams {
    const query = new URLSearchParams()

    query.append('dateType', params.dateType)
    query.append('startDate', params.startDate)
    query.append('endDate', params.endDate)
    this.appendContainerFilters(query, params)

    return query
  }

  /**
   * Recorte da rota de ANEXOS. `kind` nunca sobe: o backend força `kind=ATIVIDADE`
   * nesta rota e ignora o que o cliente mandar — repeti-lo aqui só criaria a
   * ilusão de que dá para exportar folha por esta rota. O tipo
   * já o omite; o `kind: undefined` cobre o spread de um recorte da fila com a
   * espécie preenchida, que o tipo estrutural não impede.
   */
  private buildAttachmentExportQuery(params: DSAttendanceAttachmentExportParams): URLSearchParams {
    const query = this.buildExportQuery({ ...params, kind: undefined })

    // Array REPETÍVEL e ordenado: a ordem escolhida é a ordem das partes no nome
    // do arquivo. Lista vazia = o backend aplica o default das quatro partes.
    params.nameBy?.forEach((part) => query.append('nameBy', part))

    return query
  }

  /**
   * F4 — baixa os ANEXOS dos Registros de Atividade do recorte (M3 ④).
   * Corresponde a: GET /ds-attendance/operations/containers/attachments/export
   *
   * Rota PRÓPRIA, sob a permissão `attendance_extracting` — não é a exportação
   * de registros com outro filtro. No dia do deploy só o Administrador Geral a
   * tem (`is_extractor = extractor + parent->is_general_administrator`), então
   * 403 aqui é o comportamento esperado até a Operação receber a relação.
   *
   * Só `zip` e `xlsx`: `pdf` é 400 na DTO, porque o serviço nunca monta PDF de
   * anexo e o header mentiria sobre o conteúdo.
   */
  async exportAttachments(
    params: DSAttendanceAttachmentExportParams,
    format: AttachmentExportFormat = 'zip'
  ): Promise<{ data: BlobPart; headers: Record<string, string> }> {
    try {
      const query = this.buildAttachmentExportQuery(params)
      if (format !== 'zip') query.append('format', format)

      const response = await request.get<BlobPart>(
        `/ds-attendance/operations/containers/attachments/export?${query.toString()}`,
        undefined,
        undefined,
        'blob'
      )
      return response as { data: BlobPart; headers: Record<string, string> }
    } catch (e: unknown) {
      // Mesmo unwrap do export de registros: sem ele o 400 do teto
      // (`EXPORT_TOO_LARGE`, com `attachments` e `max`) chegaria como um Blob
      // opaco e o usuário não saberia quanto reduzir o recorte.
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível exportar os anexos.')
    }
  }

  /**
   * F4 — "213 anexos · ~46 MB" ANTES do clique de baixar.
   * Corresponde a: GET /ds-attendance/operations/containers/attachments/export/estimate
   *
   * Devolve `null` em 404/405/501 (backend antigo): a estimativa é conforto, e
   * escondê-la é melhor do que impedir a exportação. 403 e 5xx continuam
   * subindo — o primeiro diz que a permissão falta, e o usuário precisa saber
   * disso antes de montar o recorte.
   */
  async getAttachmentExportEstimate(
    params: DSAttendanceAttachmentExportParams
  ): Promise<DSAttendanceAttachmentExportEstimate | null> {
    try {
      const query = this.buildAttachmentExportQuery(params)
      const response = (await request.get(
        `/ds-attendance/operations/containers/attachments/export/estimate?${query.toString()}`
      )) as { data: DSAttendanceAttachmentExportEstimate }
      return response.data ?? null
    } catch (e: unknown) {
      const status = getApiErrorStatus(e) ?? 0
      if (BACKEND_WITHOUT_ROUTE_STATUSES.includes(status)) {
        console.debug('[DsAttendance] estimativa de anexos indisponível; modal segue sem ela', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível estimar o tamanho da exportação de anexos.')
    }
  }

  /**
   * Alternar o status (Ativar/Inativar) de uma política.
   *
   * `scheduleId` é obrigatório no backend para o check de permissão no SpiceDB
   * (resource_id = schedule_id por design do schema). É enviado como query string
   * para alinhar com as outras rotas do controller (`updatePolicy`, `getPolicyById`).
   */
  async updateStatus(
    id: number | string,
    isActive: boolean,
    scheduleId?: number | string
  ): Promise<DSAttendancePolicyData> {
    try {
      const basePath = `${this.getCompletePath()}/${id}/status`
      const url = scheduleId !== undefined ? `${basePath}?scheduleId=${scheduleId}` : basePath
      const response = (await request.patch(url, { isActive })) as {
        data: DSAttendancePolicyData
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao alterar status da política.')
    }
  }

  /**
   * Aprovar container.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/approve
   */
  async approveContainer(id: number | string, payload: DSAttendanceApprovePayload): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/approve`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao aprovar o registro.')
    }
  }

  /**
   * Prévia da sobrescrita de perna (spec 2026-09-24 §6.2): os plantões deste registro que
   * já têm OUTRO registro aprovado cobrindo a mesma perna. `conflicts: []` = aprova direto.
   *
   * `legOverwrites` (sem justificativa) pede o `effectiveValue.after` das decisões dadas;
   * ausente = todas `NEW`. Devolve `null` quando a rota não existe (backend anterior): o web
   * aprova como hoje e a trava antiga, se houver, responde no próprio POST de aprovação.
   */
  async getLegOverwritePreview(
    containerId: number | string,
    legOverwrites?: DSAttendanceLegOverwriteDecision[]
  ): Promise<LegOverwritePreview | null> {
    try {
      const { data } = await request.post<Partial<LegOverwritePreview>>(
        `/ds-attendance/operations/containers/${containerId}/leg-overwrite-preview`,
        legOverwrites ? { legOverwrites } : {}
      )
      return { conflicts: Array.isArray(data?.conflicts) ? data.conflicts : [] }
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        // fallback intencional: backend sem a rota — o POST de aprovação é quem decide.
        console.debug('[DsAttendance] leg-overwrite-preview indisponível; aprovação segue', e)
        return null
      }
      throw this.handleError(
        e,
        'Não foi possível verificar os outros registros aprovados deste plantão.'
      )
    }
  }

  /**
   * Todos os registros (não rejeitados) de um plantão, com as pernas que cada um vence
   * no horário efetivo e quem sobrescreveu cada perna perdida (spec 2026-09-24 §7.1).
   */
  async getDoctorStationRecords(doctorStationId: number): Promise<DoctorStationRecordsResponse> {
    try {
      const { data } = await request.get<Partial<DoctorStationRecordsResponse>>(
        `/ds-attendance/operations/doctor-stations/${doctorStationId}/records`
      )
      return {
        timezone: typeof data?.timezone === 'string' ? data.timezone : null,
        records: Array.isArray(data?.records) ? data.records : []
      }
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível carregar os registros deste plantão.')
    }
  }

  /**
   * Reprovar container.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/reject
   */
  async rejectContainer(id: number | string, payload: DSAttendanceRejectPayload): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/reject`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao reprovar o registro.')
    }
  }

  /**
   * Iniciar auditoria (lock + AUDIT_IN_PROGRESS).
   * Corresponde a: POST /ds-attendance/operations/containers/:id/start-audit
   */
  async startAudit(id: number | string, payload?: DSAttendanceStartAuditPayload): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/start-audit`, payload || {})
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao iniciar auditoria.')
    }
  }

  async getRejectionReasons(monitoringType?: string): Promise<{ reasons: string[] }> {
    try {
      const params = monitoringType ? { monitoringType } : undefined
      const { data } = (await request.get(
        `/ds-attendance/operations/rejection-reasons`,
        params
      )) as {
        data: { reasons: string[] }
      }
      return data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao buscar motivos de reprovação.')
    }
  }

  /**
   * Confirmar conciliacao (vincular doctorStationId nas entries).
   * Corresponde a: POST /ds-attendance/operations/containers/:id/confirm-conciliation
   */
  async confirmConciliation(
    id: number | string,
    payload: DSAttendanceConfirmConciliationPayload
  ): Promise<DSAttendanceConfirmConciliationResponse> {
    try {
      const response = (await request.post(
        `/ds-attendance/operations/containers/${id}/confirm-conciliation`,
        payload
      )) as { data: DSAttendanceConfirmConciliationResponse }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao confirmar conciliação.')
    }
  }

  /**
   * Reverter validacao (Aprovado → Auditoria em Progresso, Reprovado → Aguardando Auditoria).
   * Corresponde a: POST /ds-attendance/operations/containers/:id/revert-validation
   */
  async revertValidation(id: number | string, payload: { justification: string }): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/revert-validation`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao reverter a validação.')
    }
  }

  /**
   * Status de monitoramento por vaga.
   * Corresponde a: GET /ds-attendance/operations/containers/status-by-station
   */
  async getStatusByStation(
    doctorStationId: number,
    date: string
  ): Promise<{
    hasApprovedRecord: boolean
    containerId?: number
    status?: string
    isOverdue?: boolean
    sourceApp?: string | null
  }> {
    try {
      const response = (await request.get(
        '/ds-attendance/operations/containers/status-by-station',
        { doctorStationId, date }
      )) as {
        data: {
          hasApprovedRecord: boolean
          containerId?: number
          status?: string
          isOverdue?: boolean
          sourceApp?: string | null
        }
      }
      return response.data
    } catch (e: unknown) {
      // Antes engolia qualquer falha devolvendo `{ hasApprovedRecord: false }`: um 403
      // ou um 500 virava "não há registro aprovado" e destravava a edição do plantão,
      // indistinguível de uma resposta real. Quem chama decide o estado visível.
      throw this.handleError(e, 'Não foi possível verificar o registro de presença deste plantão.')
    }
  }

  /**
   * Enviar justificativa de divergência.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/justification
   */
  async submitJustification(
    id: number | string,
    payload: { justification: string }
  ): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/justification`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao enviar justificativa.')
    }
  }

  /**
   * Aceitar justificativa de divergência.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/accept-justification
   */
  async acceptJustification(
    id: number | string,
    payload?: { justification?: string }
  ): Promise<void> {
    try {
      await request.post(
        `/ds-attendance/operations/containers/${id}/accept-justification`,
        payload || {}
      )
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao aceitar justificativa.')
    }
  }

  /**
   * Rejeitar justificativa de divergência.
   * Corresponde a: POST /ds-attendance/operations/containers/:id/reject-justification
   */
  async rejectJustification(
    id: number | string,
    payload: { justification: string }
  ): Promise<void> {
    try {
      await request.post(`/ds-attendance/operations/containers/${id}/reject-justification`, payload)
    } catch (e: unknown) {
      throw this.handleError(e, 'Erro ao rejeitar justificativa.')
    }
  }

  async downloadEvidenceFile(id: number | string): Promise<Blob> {
    try {
      const response = await request.get<Blob>(
        `/ds-attendance/operations/containers/${id}/evidence-file`,
        undefined,
        undefined,
        'blob'
      )
      return response.data
    } catch (e: unknown) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível baixar o arquivo de evidência.'
      )
    }
  }

  /* ----------------------------------------------------------------------- *
   * F2 — Registro de Atividade
   * ----------------------------------------------------------------------- */

  /** True quando o erro é "rota ainda não deployada", e não uma falha real. */
  private isRouteNotDeployed(e: unknown): boolean {
    return BACKEND_WITHOUT_ROUTE_STATUSES.includes(getApiErrorStatus(e) ?? 0)
  }

  /**
   * Envio do Registro de Atividade. `stations` vai como JSON string dentro do
   * FormData — mesmo formato de `schedules` no upload de folha.
   *
   * O erro de NEGÓCIO não é normalizado em `ServiceError` aqui: a tela escolhe o
   * texto pelo `code` do backend (§2.7), e o wrapper esconderia o `response`. Quem
   * chama usa `hasApiErrorCode`/`getApiErrorMessage` sobre o erro cru.
   *
   * A única exceção é a rota ainda não deployada: sem ela o emissor levaria "Não
   * foi possível enviar o registro." depois de cinco passos e do upload dos anexos,
   * sem saber que o problema não é dele.
   */
  async createActivityRecord(formData: FormData): Promise<ActivityRecordCreatedResponse> {
    try {
      // BF5 §6: o `PermissionsGuard` lê a chave de escopo da QUERY — o corpo
      // multipart chega vazio ao guard, então sem `?scheduleId=` a rota devolve
      // 403 sempre. O valor tem que ser o MESMO do multipart (é o que fecha a
      // anti-escalada do backend contra um corpo apontando para outra escala).
      const scheduleId = formData.get('scheduleId')
      const query =
        scheduleId !== null
          ? `?${new URLSearchParams({ scheduleId: String(scheduleId) }).toString()}`
          : ''

      const { data } = await request.post<ActivityRecordCreatedResponse>(
        `/ds-attendance/operations/activity-records${query}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        // `ServiceError` preserva o erro original em `originalError`, então os
        // guards de status/código de quem chama continuam funcionando.
        throw new ServiceError(
          'O envio de Registro de Atividade ainda não está disponível nesta versão. ' +
            'Tente novamente mais tarde.',
          e
        )
      }
      throw e
    }
  }

  /**
   * "O que o sistema já tem" para os plantões escolhidos (§2.4) — o passo 4 do
   * modal compara o declarado com isto antes de o médico confirmar.
   *
   * `scheduleId` é OBRIGATÓRIO (B17 §1.1): é a chave de escopo que o `PermissionsGuard`
   * usa para resolver o recurso. Quem filtra os plantões é o `@AccessibleResources`, mas
   * sem o `scheduleId` na query o guard nem chega ao serviço — a chamada morre em 403.
   * Sem ele aqui, nem sai da rede: uma lista vazia é o mesmo default seguro da rota
   * ausente, e melhor que um 403 no console a cada abertura do passo 4.
   */
  async getActivityRecordPrefill(
    scheduleId: number | string,
    doctorStationIds: number[]
  ): Promise<ActivityRecordPrefillItem[]> {
    if (!scheduleId) {
      console.debug('[DsAttendance] prefill sem scheduleId; a rota exige a chave de escopo')
      return []
    }

    try {
      const query = new URLSearchParams({ scheduleId: String(scheduleId) })
      doctorStationIds.forEach((id) => query.append('doctorStationIds', String(id)))

      const { data } = await request.get<{ items: ActivityRecordPrefillItem[] }>(
        `/ds-attendance/operations/activity-records/prefill?${query.toString()}`
      )
      return data?.items ?? []
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] prefill do registro indisponível; seguindo sem ele', e)
        return []
      }
      throw this.handleError(e, 'Não foi possível consultar o que o sistema já tem deste plantão.')
    }
  }

  /**
   * "Adicionar outros plantões" do passo 2 (BF16, Q15) — outros plantões do
   * MESMO médico da vaga de origem, nos últimos `days` dias.
   *
   * Tolerante a backend antigo: 404/405/501 caem em lista vazia, o mesmo
   * default seguro do prefill — a seção só fica vazia, nunca quebra o passo 2.
   */
  async getActivityRecordCandidates(
    doctorStationId: number,
    days = 30
  ): Promise<ActivityRecordCandidateStation[]> {
    if (!doctorStationId) return []

    try {
      const query = new URLSearchParams({
        doctorStationId: String(doctorStationId),
        days: String(days)
      })
      const { data } = await request.get<ActivityRecordCandidatesResponse>(
        `/ds-attendance/operations/activity-records/candidates?${query.toString()}`
      )
      return data?.stations ?? []
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] candidatos indisponíveis; "outros plantões" fica vazio', e)
        return []
      }
      throw this.handleError(e, 'Não foi possível buscar outros plantões deste médico.')
    }
  }

  /**
   * Config de registro servida pelo BACKEND (flag global + política da escala).
   * Tolerante a backend antigo: 404/405/501 caem no default seguro e a tela
   * renderiza exatamente como hoje — o web deploya antes.
   */
  async getRegistrationSettings(scheduleId?: number): Promise<RegistrationSettings> {
    try {
      const { data } = await request.get<Partial<RegistrationSettings> | null>(
        '/ds-attendance/operations/registration-settings',
        scheduleId ? { scheduleId } : undefined
      )

      // Merge com o default: um backend que responda só `{ manualCheckBlockEnabled }`
      // entregaria `acceptedActivityTypes` undefined a quem faz `.includes`/`.length`.
      const merged = { ...createDefaultRegistrationSettings(), ...(data ?? {}) }

      return {
        ...merged,
        // `null` é resposta válida do backend ("todos os tipos do default"), mas o
        // cliente nunca pode receber null/undefined aqui.
        acceptedActivityTypes: merged.acceptedActivityTypes ?? [],
        // Feature detection: só um 200 que TRAZ a flag prova que a F2 do backend
        // está no ar. Sem ela, tudo o que depende da fase segue desligado.
        isBackendAvailable: typeof data?.activityRecordsEnabled === 'boolean'
      }
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] registration-settings indisponível; usando default seguro', e)
        return createDefaultRegistrationSettings()
      }
      throw this.handleError(e, 'Não foi possível carregar as configurações de registro.')
    }
  }

  /**
   * Contagem da fila por espécie, para o segmented control `Todos/Folha/Atividade`.
   * `null` quando a rota não existe: o controle fica sem os números, nunca com
   * números errados.
   */
  async getCountsByKind(
    params: ListContainersByCompanyParams
  ): Promise<ContainerCountsByKind | null> {
    try {
      // Sem `kind` e sem `onlyRequests`: a contagem é sempre de TODAS as abas sobre os
      // mesmos demais filtros — herdar o recorte da aba ativa zeraria as outras.
      const query = this.buildContainerListQuery({
        ...params,
        kind: undefined,
        onlyRequests: undefined
      })
      const { data } = await request.get<ContainerCountsByKind>(
        `/ds-attendance/operations/containers/counts-by-kind?${query.toString()}`
      )
      return data ?? null
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] counts-by-kind indisponível; abas sem contagem', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível contar os registros por espécie.')
    }
  }

  /**
   * Onda 1 (spec 2026-09-25 §4.7) — aba Solicitações no Contrato de Visualizações do Módulo.
   * Corresponde a: GET /ds-attendance/operations/activity-requests
   */
  async listActivityRequests(
    query: ActivityRequestQuery
  ): Promise<ModuleViewListResponse<ActivityRequestPayload>> {
    try {
      const params = buildActivityRequestParams(query, { paginate: true, includeView: true })
      const { data } = await request.get<ApiActivityRequestListResponse>(
        `${ACTIVITY_REQUESTS_API_PATH}?${params.toString()}`
      )
      return mapActivityRequestListResponse(data)
    } catch (e: unknown) {
      throw this.handleError(
        e,
        'Não foi possível carregar as solicitações de Registro de Atividade.'
      )
    }
  }

  /**
   * Contadores das quick views: respeitam TODOS os filtros menos `view` (§4.7), por isso a
   * query sai sem ela. Corresponde a: GET .../activity-requests/counters
   */
  async getActivityRequestCounters(query: ActivityRequestQuery): Promise<ModuleViewCounters> {
    try {
      const params = buildActivityRequestParams(query, { paginate: false, includeView: false })
      const { data } = await request.get<ApiActivityRequestCounters>(
        `${ACTIVITY_REQUESTS_API_PATH}/counters?${params.toString()}`
      )
      return mapActivityRequestCounters(data)
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível contar as solicitações.')
    }
  }

  /** Corresponde a: GET .../activity-requests/:id/timeline (cadeia + audits, anti-IDOR no BE). */
  async getActivityRequestTimeline(containerId: number): Promise<ActivityRequestTimelineResponse> {
    try {
      const { data } = await request.get<ApiActivityRequestTimelineResponse>(
        `${ACTIVITY_REQUESTS_API_PATH}/${containerId}/timeline`
      )
      return mapActivityRequestTimelineResponse(data)
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível carregar o histórico da solicitação.')
    }
  }

  /**
   * XLSX com o recorte inteiro (sem paginação). 400 `EXPORT_TOO_LARGE` acima de 20.000
   * linhas: a mensagem do backend vem dentro do Blob, daí o `unwrapBlobError`.
   */
  async exportActivityRequests(query: ActivityRequestQuery): Promise<void> {
    try {
      const params = buildActivityRequestParams(query, { paginate: false, includeView: true })
      const { data, headers } = await request.get<BlobPart>(
        `${ACTIVITY_REQUESTS_API_PATH}/export?${params.toString()}`,
        undefined,
        undefined,
        'blob'
      )
      const disposition = (headers?.['content-disposition'] as string | undefined) ?? ''
      const filename =
        disposition.match(/filename="?([^"]+)"?/)?.[1] ?? 'solicitacoes-registro-de-atividade.xlsx'
      const url = window.URL.createObjectURL(
        new Blob([data], { type: ACTIVITY_REQUESTS_XLSX_CONTENT_TYPE })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e: unknown) {
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível exportar as solicitações.')
    }
  }

  /**
   * Simulação do horário efetivo com o valor que o auditor está prestes a gravar
   * (M3): responde "isto vence o ranking?" ANTES de aprovar.
   *
   * Os parâmetros são `checkin`/`checkout` (B17 §1.1 rota 5) — o `declaredCheckin`/
   * `declaredCheckout` que a W1 tipou não existe na rota e seria descartado pelo
   * `whitelist` do backend, devolvendo uma prévia do valor JÁ GRAVADO em vez do valor
   * que o auditor está digitando.
   *
   * Devolve o envelope CRU, e `null` — nunca `[]` — quando a rota não está no ar: é o
   * que deixa a tela distinguir "a prévia não respondeu" de "respondeu e nada muda"
   * (I8), e é do envelope que saem `precedence` e `registrationStatus`.
   */
  async getEffectivePreview(
    containerId: number | string,
    params: { checkin?: string | null; checkout?: string | null }
  ): Promise<EffectivePreviewEnvelope | null> {
    try {
      const { data } = await request.get<EffectivePreviewEnvelope>(
        `/ds-attendance/operations/containers/${containerId}/effective-preview`,
        params
      )
      return data ?? null
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] effective-preview indisponível; auditoria segue sem ele', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível simular o horário efetivo deste registro.')
    }
  }

  /**
   * Valor do auditor de um Registro de Atividade (§3.4 regra 2). É o `PATCH` de entry que
   * já existe em produção — `containerId` na query porque é ele que o `PermissionsGuard`
   * usa para resolver a permissão e o que restringe a busca da entry à chave composta.
   *
   * Os horários vão em ISO 8601 **com o offset do plantão** (B17 §4.5): o `@Type(() => Date)`
   * do DTO aceita ISO com ou sem offset, e sem ele o Node interpreta a string como hora local
   * DO SERVIDOR — o instante gravado passaria a depender do fuso do processo.
   *
   * `auditorOverrideReason` é obrigatório no BACKEND quando o valor gravado difere do
   * declarado (400 `ACTIVITY_OVERRIDE_REASON_REQUIRED`); a trava da tela é só ergonomia.
   * Aceita `null` (e não só `undefined`) porque voltar ao declarado precisa ZERAR o motivo
   * da sobrescrita anterior — omitir o campo deixaria na entry um motivo sem sobrescrita.
   * Sem tolerância a rota ausente de propósito: um backend sem a F2 não tem container
   * `ATIVIDADE` nenhum, logo esta chamada não acontece — e engolir a falha aqui gravaria
   * "aprovado" sobre um valor que o backend não aceitou.
   */
  async updateActivityEntry(
    entryId: number | string,
    containerId: number | string,
    payload: {
      checkinReal?: string | null
      checkoutReal?: string | null
      auditorOverrideReason?: string | null
    }
  ): Promise<void> {
    try {
      await request.patch(
        `/ds-attendance/operations/entries/${entryId}?containerId=${containerId}`,
        payload
      )
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível gravar o valor do auditor.')
    }
  }

  /**
   * Anexo de um Registro de Atividade, pelo proxy com marca d'água e anti-IDOR.
   * Devolve Blob — nunca uma URL assinada do storage (§9.3).
   *
   * O caminho termina em `/file` (B17 §1.1 rota 6): sem o sufixo a rota não existe e o
   * visualizador abriria vazio com um 404 no console. Serve QUALQUER anexo ligado ao
   * container — a folha também é anexo — e responde 404 quando o anexo não é dele ou o
   * blob sumiu do storage.
   */
  async getAttachmentFile(
    containerId: number | string,
    evidenceId: number | string
  ): Promise<Blob> {
    try {
      const response = await request.get<Blob>(
        `/ds-attendance/operations/containers/${containerId}/attachments/${evidenceId}/file`,
        undefined,
        undefined,
        'blob'
      )
      return response.data
    } catch (e: unknown) {
      throw this.handleError(await unwrapBlobError(e), 'Não foi possível abrir o anexo.')
    }
  }

  /**
   * Entries do container (`GET containers/:id/entries`). Um Registro de Atividade tem UMA
   * entry, e é nela que mora o valor já GRAVADO pelo auditor (`checkinReal`/`checkoutReal`/
   * `auditorOverrideReason`) — a estação de auditoria semeia o campo com ele, e não com o
   * declarado, quando o auditor reabre um registro que já sobrescreveu (achado R2-C2).
   * `[]` quando a rota não existe: a tela cai no declarado.
   *
   * Fuso: a partir da BF12 os horários vêm como ISO COM o offset do plantão (parede,
   * regra BF7). O backend anterior devolvia a entidade crua — coluna `timestamp` sem fuso
   * lida no `TZ` do processo e serializada com `Z`, certa só em Brasília. A costura
   * (`auditorLegOf` em `activity-audit-detail.ts`) trata os dois formatos.
   */
  async getContainerEntries(containerId: number | string): Promise<DSAttendanceActivityEntry[]> {
    try {
      const { data } = await request.get<{ data?: DSAttendanceActivityEntry[] } | null>(
        `/ds-attendance/operations/containers/${containerId}/entries`
      )
      return data?.data ?? []
    } catch (e: unknown) {
      if (this.isRouteNotDeployed(e)) {
        console.debug('[DsAttendance] entries indisponível; auditoria segue com o declarado', e)
        return []
      }
      throw this.handleError(e, 'Não foi possível carregar o lançamento do registro.')
    }
  }

  /**
   * Declarado, anexos e efetivo de um Registro de Atividade
   * (`GET containers/:id/activity-record`, BF11). É um recorte byte-compatível de
   * `GET /stations/:dsId/registration` — `activityRecords[]` só com o item deste
   * container, mais `legs` e `frozen` — servido sob o MESMO guard de `GET containers/:id`.
   * A auditoria lê daqui, e não da registration, porque a registration exige leitura da
   * ESCALA: o auditor comum (receptor do ds_attendance na empresa/unidade) tomava 403 e a
   * tela abria vazia. 403/404 (não é ATIVIDADE) sobem como erro normalizado: a tela mostra
   * o toast e segue com o que o container e a fila têm.
   */
  async getActivityRecord(containerId: number | string): Promise<DSAttendanceActivityRecord> {
    try {
      const { data } = await request.get<DSAttendanceActivityRecord>(
        `/ds-attendance/operations/containers/${containerId}/activity-record`
      )
      return data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível carregar o declarado deste registro.')
    }
  }
}

/**
 * `GET containers/:id/activity-record` — subconjunto da registration do plantão, no
 * recorte que a auditoria costura (`buildActivityAuditDetail`). Tudo opcional além do
 * envelope: o web deploya antes e nenhuma ausência pode derrubar a tela.
 */
export interface DSAttendanceActivityRecord {
  doctorStationId?: number | null
  timezone?: string | null
  registrationStatus?: string | null
  registrationStatusLabel?: string | null
  activityRecords?: Array<Record<string, unknown>> | null
  legs?: Record<string, unknown> | null
  frozen?: { at?: string | null; reason?: string | null } | boolean | null
}

/** A entry de um Registro de Atividade, no recorte que a auditoria lê. */
export interface DSAttendanceActivityEntry {
  id: number
  containerId?: number
  doctorStationId?: number | null
  checkinReal: string | null
  checkoutReal: string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  coveredLegs?: CoveredLegs | string | null
  activityType?: ActivityType | string | null
  activityDescription?: string | null
  auditorOverrideReason?: string | null
}
